import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getMenuInventoryProductsPage } from 'redux/actions/inventory/inventory';

/** Pages fetched ahead of the shown one, so paging forward has no delay. */
const PREFETCH_PAGES = 2;

/**
 * Server-side pages of the menu inventory list.
 *
 * `query` is `{ page (0-based), pageSize, categoryId, search, statuses, sortBy,
 * sortOrder, enabled }`; `categoryId` is null for the whole menu; nothing is
 * fetched while `enabled` is false.
 * Pages of the current filters/sort are cached; every time a page is shown the
 * next `PREFETCH_PAGES` are fetched into the cache. Changing filters, sort or
 * page size, or calling `refresh()` (after a mutation), drops the cache.
 */
const useMenuInventoryPages = ({
  page,
  pageSize,
  categoryId,
  search,
  statuses,
  sortBy,
  sortOrder,
  enabled,
}) => {
  const dispatch = useDispatch();
  const cacheRef = useRef(new Map());
  const cachedFiltersKeyRef = useRef(null);
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const filtersKey = JSON.stringify({
    pageSize,
    categoryId,
    search,
    statuses,
    sortBy,
    sortOrder,
  });

  const fetchPage = useCallback(
    (key, pageIndex) => {
      const cache = cacheRef.current;
      const cacheKey = `${key}|${pageIndex}`;
      if (cache.has(cacheKey)) return cache.get(cacheKey);

      const { pageSize: limit, ...rest } = JSON.parse(key);
      const request = dispatch(
        getMenuInventoryProductsPage({
          page: pageIndex + 1,
          limit,
          ...rest,
        })
      );
      cache.set(cacheKey, request);
      request.catch(() => {
        if (cache.get(cacheKey) === request) cache.delete(cacheKey);
      });
      return request;
    },
    [dispatch]
  );

  useEffect(() => {
    if (!enabled) return undefined;
    if (cachedFiltersKeyRef.current !== filtersKey) {
      cacheRef.current.clear();
      cachedFiltersKeyRef.current = filtersKey;
    }

    let active = true;
    setLoading(true);
    fetchPage(filtersKey, page).then(
      (result) => {
        if (!active) return;
        setData(result);
        setError(null);
        setLoading(false);
        for (let ahead = 1; ahead <= PREFETCH_PAGES; ahead += 1) {
          if (page + ahead >= result.totalPages) break;
          fetchPage(filtersKey, page + ahead).catch((err) =>
            console.error('Error prefetching inventory page:', err)
          );
        }
      },
      (err) => {
        if (!active) return;
        setError(err);
        setLoading(false);
      }
    );
    return () => {
      active = false;
    };
  }, [enabled, fetchPage, filtersKey, page, revision]);

  const refresh = useCallback(() => {
    cacheRef.current.clear();
    setRevision((value) => value + 1);
  }, []);

  return { data, loading, error, refresh };
};

export default useMenuInventoryPages;
